import { StatusBadge } from "@/components/StatusBadge";
import { formatMm } from "@/lib/format";
import { closureText, outlinePath, type DemoOutline } from "@/lib/outline";

import styles from "./OutlinePicker.module.css";

const THUMB = { width: 190, height: 130, padding: 16 };

/**
 * Which shape do the read-off Teilzüge make?
 *
 * The backend enumerates every way the disconnected partial runs could chain
 * into a rectangular house outline and ranks the candidates by how closely
 * each one closes (Schließungsgüte) — this picker only draws that list and
 * hands the clicked id back. Ranking is plausibility, not a decision, and the
 * order here is never a recommendation.
 *
 * **A shape may arrive already marked.** Since 12.08.2026 the backend draws
 * an unambiguous reading of its own accord (`chosenBy: "system"`, design spec
 * §2), and that candidate comes back with `chosen: true` — `aria-pressed`
 * says so, because a screen that hid the machine's own pick would be worse
 * than one that shows it. What has not changed is A-014: drawn is not
 * confirmed. A shape counts as confirmed only once a person says so, in
 * `OutlineReview`, and nothing on the wire can say it already happened. The
 * best-closing candidate carries a badge saying so —
 * a mark, not a selection. The unconnected arrangement, the Teilzüge left as
 * they are, is offered on equal footing in its own words ("getrennte Läufe")
 * rather than as a failure, because leaving them separate is a valid reading
 * of the drawing, not a fallback for one that could not be understood.
 *
 * **A section, not a panel** (review finding, 10.08.2026). This used to be a
 * `Panel` rendered inside `OutlineReview`'s own `Panel`: two `<h2>`s of equal
 * rank, so a screen reader heard the picker as the review's neighbour rather
 * than as part of it, and five framed levels stacked with `--space-5` inside
 * `--space-5`. It is one step of the review, so it is an `<h3>` and a list
 * inside the review's card. The empty case is gone with the frame: the review
 * returns before rendering this when there is nothing to choose from, so the
 * "keine Anordnung gefunden" state exists in exactly one place.
 *
 * Whether a shape reads as closing comes straight off `closed` — never
 * re-derived from `gapMm === 0`, and not from `closureGap(...) === null`
 * either (review finding, 10.08.2026: an earlier version of this comment
 * blamed the unconnected arrangement's placeholder zero for that one, but
 * `closureGap` checks `!connected` first, so that case never reaches the
 * comparison at all). For a *connected* candidate, `closureGap(...) ===
 * null` is arithmetically the same test as `gapMm === 0` under a different
 * name — and that test is not "closed": a run walked out and folded
 * straight back over itself also reaches `gapMm === 0` without enclosing
 * anything, which is exactly why the backend derives `closed` from an area
 * check instead of a distance. The field has already been added to two
 * lower layers for this reason — the backend's `OutlineCandidate.closed`
 * (Task 5), then the wire type `OutlineOut.closed` (Task 6) — and
 * re-deriving it here from either shortcut would reopen the same defect one
 * layer further out, at the one place meant only to display it. (The area
 * check itself still has a narrower, accepted gap for a rarer shape —
 * `docs/superpowers/specs/2026-08-09-rostschema-zeichnung-design.md` §6
 * Grenze 6 — but that is the backend's residual risk to carry, not a reason
 * to re-derive anything here.)
 */
export function OutlinePicker({
  outlines,
  onChoose,
  busy = false,
  unavailable = null,
}: {
  outlines: DemoOutline[];
  onChoose: (id: string) => void;
  busy?: boolean;
  /**
   * Why these shapes cannot be chosen right now — `null` while they can.
   *
   * Choosing a shape starts a new run scoped to its id. After a failed run
   * the list on screen belongs to the run before it, so a click asks for an
   * id the backend may no longer know and walks into the same 422 the reader
   * just saw (review finding N-1, 10.08.2026). Disabled *and* said out loud:
   * a dead card that merely looks dead teaches nothing.
   */
  unavailable?: string | null;
}) {
  // Nothing to pick from is `OutlineReview`'s state to show, not this one's —
  // it returns before rendering a picker at all. Rendering nothing here keeps
  // a defensive path from growing a second, differently worded empty state.
  if (outlines.length === 0) return null;

  // The first connected candidate in the backend's own order — the ranking
  // by Schließungsgüte is the backend's, not recomputed here. `find` rather
  // than `outlines[0]`: if nothing connects at all, there is no "best
  // closure" to mark, only the separate arrangement.
  //
  // And only where there is something to be best *among*. One connected shape
  // on its own was never ranked against anything — a contour the model
  // described is the plain case (backend `autoReason: "contour"`), and so is
  // a single arrangement. "Beste Schließung" there states a comparison that
  // did not happen, which is the sort of claim this screen exists to avoid.
  const connected = outlines.filter((shape) => shape.connected);
  const best = connected.length > 1 ? connected[0] : undefined;

  return (
    // No `aria-labelledby` on the section: naming a `<section>` promotes it to
    // a region landmark, and a step inside a card is not a landmark. The `<h3>`
    // carries the structure, which is what was missing.
    <section className={styles.section}>
      <div className={styles.head}>
        <h3 className={styles.title}>Welche Form ist es?</h3>
        <span className={`label ${styles.hint}`}>
          gerechnet, nicht geraten · {outlines.length}{" "}
          {outlines.length === 1 ? "Kandidat" : "Kandidaten"}
        </span>
      </div>

      {unavailable ? <p className={styles.unavailable}>{unavailable}</p> : null}

      <ul className={styles.list} aria-busy={busy}>
        {outlines.map((shape) => (
          <li key={shape.id}>
            <button
              type="button"
              className={styles.option}
              disabled={busy || unavailable !== null}
              aria-pressed={shape.chosen}
              // The whole card in one sentence. Without it the read-out name
              // is the thumb's label, the badge and the two sums concatenated
              // into an unstructured run of words — and the card's own markup
              // has to stay phrasing content, which a `<dl>` is not (review
              // findings I10/M2, 10.08.2026).
              aria-label={shapeLabel(shape, shape === best)}
              onClick={() => onChoose(shape.id)}
            >
              {shape.connected ? (
                <Thumb outline={shape} />
              ) : (
                <>
                  <span className={styles.separateMark} aria-hidden="true" />
                  <span className={styles.caption}>Getrennte Läufe</span>
                </>
              )}

              {shape === best ? <StatusBadge status="auto">Beste Schließung</StatusBadge> : null}

              {shape.connected ? (
                <span className={styles.sums}>
                  <span className={styles.sum}>
                    <span className="label">Σh</span>
                    <span className="num">{formatMm(shape.sumHorizontalMm)}</span>
                  </span>
                  <span className={styles.sum}>
                    <span className="label">Σv</span>
                    <span className="num">{formatMm(shape.sumVerticalMm)}</span>
                  </span>
                </span>
              ) : null}

              <span className={`num ${styles.gap}`}>{closureText(shape)}</span>
            </button>
          </li>
        ))}
      </ul>
    </section>
  );
}

/**
 * What `corners` counts, said in words that match it.
 *
 * It is **not** the number of corners the drawn shape has: the backend counts
 * the joints *between* Teilzüge that the line turns a right angle at (plus the
 * closing seam), and a bend inside one Teilzug is not among them
 * (`OutlineCandidate.corners`, and the review finding of 10.08.2026 that this
 * label used to claim otherwise). It is the figure the geteilte Eckkonsole is
 * a rule about, so it is worth saying — under its own name.
 */
function cornerWord(count: number): string {
  if (count === 0) return "kein Stoß mit Ecke";
  return `${count} ${count === 1 ? "Stoß" : "Stöße"} mit Ecke`;
}

/** Dative — its one caller reads "Form aus …" (review finding, 10.08.2026). */
function wallWord(count: number): string {
  return `${count} ${count === 1 ? "Abschnitt" : "Abschnitten"}`;
}

/** The card as one sentence, for the button's accessible name. */
function shapeLabel(shape: DemoOutline, best: boolean): string {
  if (!shape.connected) {
    return "Getrennte Läufe — die Teilzüge bleiben, wie sie gelesen wurden, keine Prüfung möglich";
  }
  const parts = [
    `Form aus ${wallWord(shape.segments.length)}`,
    cornerWord(shape.corners),
    closureText(shape),
  ];
  if (best) parts.push("beste Schließung");
  return parts.join(", ");
}

function Thumb({ outline }: { outline: DemoOutline }) {
  const path = outlinePath(outline, THUMB);

  // No `role="img"` and no label of its own: the button around it carries the
  // description now, and a second name inside it would be either ignored or
  // read twice depending on the reader.
  return (
    // A drawing of the shape, and so a sheet (design 2026-10-24 §5.3): warm
    // paper in both modes (B-20), the card around it in the mode.
    <svg className={styles.thumb} data-blatt="zeichnung" viewBox={`0 0 ${THUMB.width} ${THUMB.height}`} aria-hidden="true">
      <path d={path} fill="none" stroke="var(--ink)" strokeWidth={2} strokeLinejoin="round" />
    </svg>
  );
}
